import { Pencil } from 'lucide-react';
import { useState } from 'react';
import UpdateGeometryModal from './modals/UpdateGeometryModal';
import { useDispatch } from 'react-redux';
import { fetchDetail, updateTypeMarkBasicData } from '@/store/actions/typemark';
import { useQueryClient } from 'react-query';
import ImageCropperDialog from '../../../../components/ImageCropperDialog';
import { InstanceTypeResponse } from '../hooks/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

type GeometrySwitchProps = {
  typeMark: InstanceTypeResponse;
  geometry: any;
};

const GeometrySwitch = ({ typeMark, geometry }: GeometrySwitchProps) => {
  const [showGeometryViewDialog, setShowGeometryViewDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const viewMode = typeMark.view_mode || (typeMark.is_geo ? 'geometry' : 'image');
  const currentViewMode: 'geometry' | 'image' =
    viewMode === 'layers' ? 'geometry' : (viewMode as 'geometry' | 'image');

  const handleSwitch = (mode: 'geometry' | 'image') => {
    if (currentViewMode === mode) return;
    dispatch(
      updateTypeMarkBasicData(
        typeMark.id,
        { view_mode: mode, is_geo: mode === 'geometry' },
        queryClient
      )
    );
  };

  const handleUploadImage = () => {
    dispatch(fetchDetail(typeMark.id));
    setShowImageDialog(false);
  };

  const handleEdit = () => {
    if (currentViewMode === 'geometry') setShowGeometryViewDialog(true);
    else setShowImageDialog(true);
  };

  return (
    <>
      <div className="h-9 flex items-center justify-between w-full">
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={() => handleSwitch('geometry')}
            data-testid="typemark-geometry-switch-geometry-btn"
            className={cn(
              'relative h-9 text-sm font-medium transition-colors',
              currentViewMode === 'geometry'
                ? 'text-neutral-900'
                : 'text-neutral-400 hover:text-neutral-700'
            )}
          >
            Geometrie
            {currentViewMode === 'geometry' && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary-600 rounded-full" />
            )}
          </button>
          <button
            type="button"
            onClick={() => handleSwitch('image')}
            data-testid="typemark-geometry-switch-image-btn"
            className={cn(
              'relative h-9 text-sm font-medium transition-colors',
              currentViewMode === 'image'
                ? 'text-neutral-900'
                : 'text-neutral-400 hover:text-neutral-700'
            )}
          >
            Obrázek
            {currentViewMode === 'image' && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary-600 rounded-full" />
            )}
          </button>
        </div>

        <Button
          variant="ghost"
          size="icon-sm"
          className="text-neutral-300 hover:text-neutral-700"
          data-testid="typemark-geometry-edit-btn"
          onClick={handleEdit}
        >
          <Pencil size={12} />
        </Button>
      </div>

      {showGeometryViewDialog && (
        <UpdateGeometryModal
          visible={showGeometryViewDialog}
          typeMarkId={typeMark.id}
          geometry={geometry?.geo}
          position={geometry?.position}
          perspective={geometry?.perspective}
          layers={geometry?.layers}
          zoom={geometry?.zoom}
          editable
          onClose={() => setShowGeometryViewDialog(false)}
        />
      )}

      {showImageDialog && (
        <ImageCropperDialog
          visible={showImageDialog}
          onSubmit={() => handleUploadImage()}
          method="patch"
          url={`/api/v1/instance-type/${typeMark.id}/upload-picture/`}
          onClose={() => setShowImageDialog(false)}
          aspectRatio={1}
        />
      )}
    </>
  );
};

export default GeometrySwitch;
